import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { patients as patientsApi, visits as visitsApi } from '@/api/endpoints'
import { ApiError } from '@/api/errors'
import { queryKeys } from '@/api/queryKeys'
import type { PatientResponse, VisitPage } from '@/api/types'
import { RecordTable, type RecordColumn } from '@/components/RecordTable'
import { Banner } from '@/components/Banner'
import { Breadcrumb } from '@/components/Breadcrumb'
import { Button } from '@/components/Button'
import { EmptyState } from '@/components/EmptyState'
import { Icon } from '@/components/Icon'
import { LinkButton } from '@/components/LinkButton'
import { Spinner } from '@/components/Spinner'
import { Tabs, type TabDef } from '@/components/Tabs'
import { AttachmentStrip } from '@/features/attachments/AttachmentStrip'
import { useRecentPatients } from '@/features/search/useRecentPatients'
import { cx } from '@/lib/cx'
import { formatVisitDate, todayIso } from '@/lib/dates'
import { PatientRecordHeader } from './PatientRecordHeader'
import { ConsultationPanel } from '@/features/visit/ConsultationPanel'
import { VisitCard } from './VisitCard'
import { errorMessage } from '@/api/messages'
import { groupByVisit } from '@/lib/prescriptions'

const PAGE_SIZE = 20

/** A prescription line and a test result, as this record reads them back. The
 *  visit date is in the group heading, so it is not repeated on every row. */
type RecordPrescription = { drug_name: string; dose: string | null; frequency: string | null;
  duration: string | null; instructions: string | null }
type RecordTest = { test_name: string; result: string | null; recorded_on: string | null;
  visitDate: string }

const RECORD_PRESCRIPTION_COLUMNS: RecordColumn<RecordPrescription>[] = [
  {
    key: 'drug',
    header: 'Medicine',
    primary: true,
    className: 'font-semibold text-fg',
    cell: (item) => item.drug_name,
  },
  { key: 'dose', header: 'Dose', cell: (item) => item.dose },
  { key: 'frequency', header: 'Frequency', cell: (item) => item.frequency },
  { key: 'duration', header: 'Duration', cell: (item) => item.duration },
  { key: 'remarks', header: 'Remarks', cell: (item) => item.instructions },
]

const RECORD_TEST_COLUMNS: RecordColumn<RecordTest>[] = [
  {
    key: 'test',
    header: 'Test',
    primary: true,
    className: 'font-semibold text-fg',
    cell: (item) => item.test_name,
  },
  { key: 'result', header: 'Result', cell: (item) => item.result },
  {
    key: 'date',
    header: 'Date',
    className: 'whitespace-nowrap text-fg-subtle',
    cell: (item) => formatVisitDate(item.recorded_on ?? item.visitDate),
  },
]

type TabId = 'consultation' | 'history' | 'prescriptions' | 'tests' | 'documents'

interface Props {
  /** Set when arriving from "New consultation" or from editing one. */
  initialTab?: TabId
  patientIdOverride?: string
  visitId?: string | null
}

export function PatientScreen({ initialTab, patientIdOverride, visitId = null }: Props = {}) {
  const params = useParams()
  const patientId = patientIdOverride ?? params.patientId ?? ''
  const { remember } = useRecentPatients()
  const [tab, setTab] = useState<TabId>(initialTab ?? 'history')
  // null means "whichever is newest"; '' means the doctor closed it.
  const [openVisit, setOpenVisit] = useState<string | null>(null)
  const navigate = useNavigate()

  // React reconciles this component across /patients/:id and .../visits/new, so
  // the initial state never re-applies on its own. It falls back rather than
  // only applying a defined initialTab: saving navigates back to /patients/:id,
  // and a still-populated form there would write a second visit.
  useEffect(() => {
    setTab(initialTab ?? 'history')
  }, [initialTab, visitId])

  const patient = useQuery<PatientResponse, ApiError>({
    queryKey: queryKeys.patient(patientId),
    queryFn: ({ signal }) => patientsApi.get(patientId, signal),
    enabled: Boolean(patientId),
  })

  const history = useInfiniteQuery<VisitPage, ApiError>({
    queryKey: queryKeys.visits(patientId),
    queryFn: ({ pageParam, signal }) =>
      visitsApi.list(patientId, PAGE_SIZE, (pageParam as string | null) ?? null, signal),
    initialPageParam: null,
    getNextPageParam: (last) => last.next_cursor,
    enabled: Boolean(patientId),
  })

  useEffect(() => {
    if (!patient.data) return
    void remember({
      id: patient.data.id,
      op_number: patient.data.op_number,
      full_name: patient.data.full_name,
      age: patient.data.age,
      sex: patient.data.sex,
      last_visit_at: patient.data.last_visit_at,
    })
  }, [patient.data, remember])

  if (patient.isLoading) return <Spinner label="Loading patient" />

  if (patient.error) {
    return (
      <main className="w-full max-w-wide px-4 pt-4 pb-6 lg:px-6 lg:pt-6 lg:pb-8">
        <Banner tone={patient.error.isNetwork ? 'warning' : 'danger'}>
          {patient.error.isNetwork
            ? `${errorMessage(patient.error)} This patient cannot be loaded until it returns.`
            : errorMessage(patient.error)}
        </Banner>
        <div className="mt-4">
          <LinkButton to="/patients" variant="secondary">
            Back to patients
          </LinkButton>
        </div>
      </main>
    )
  }

  if (!patient.data) return null

  const visits = history.data?.pages.flatMap((page) => page.data) ?? []
  const prescriptions = visits.flatMap((v) =>
    v.prescription.map((item) => ({ ...item, visitDate: v.visit_date, visitId: v.id })),
  )
  const tests = visits.flatMap((v) =>
    v.test_results.map((item) => ({ ...item, visitDate: v.visit_date, visitId: v.id })),
  )
  const documents = visits.flatMap((v) => v.attachments)

  // From the server, with the first page. The history is paged, so counting
  // what is loaded would label the tabs with how far the doctor has scrolled
  // rather than with what the record holds — and it would climb as they did.
  const counts = history.data?.pages[0]?.counts ?? null

  const TABS: TabDef<TabId>[] = [
    { id: 'consultation', label: 'Consultation' },
    { id: 'history', label: 'History', count: counts?.visits },
    { id: 'prescriptions', label: 'Prescriptions', count: counts?.prescriptions },
    { id: 'tests', label: 'Test results', count: counts?.test_results },
    { id: 'documents', label: 'Documents', count: counts?.documents },
  ]

  return (
    <div className="flex min-h-full flex-col">
      <main className="flex flex-1 flex-col gap-4 w-full max-w-wide px-4 pt-4 pb-6 lg:px-6 lg:pt-6 lg:pb-8">
        <Breadcrumb
          trail={[{ label: 'Patients', to: '/patients' }, { label: patient.data.op_number }]}
        />

        <PatientRecordHeader patient={patient.data} visitCount={counts?.visits} />

        <Tabs
            tabs={TABS}
            active={tab}
            onChange={setTab}
            label="Patient record"
            aside={
              tab === 'consultation' ? (
                <>
                  <span className="flex h-field items-center gap-2 rounded-md border border-line bg-subtle px-3 text-sm whitespace-nowrap text-fg-muted">
                    <Icon name="calendar" size="1rem" />
                    {formatVisitDate(todayIso())}
                  </span>
                  <Button
                    variant="destructiveOutline"
                    onClick={() => {
                      setTab('history')
                      navigate(`/patients/${patientId}`, { replace: true })
                    }}
                  >
                    Discard
                  </Button>
                </>
              ) : null
            }
          />

        <div
          className={
            tab === 'consultation'
              ? ''
              : cx(
                  'overflow-hidden rounded-lg border border-line-soft bg-surface',
                  tab !== 'history' && 'p-5',
                )
          }
          role="tabpanel"
          id={`panel-${tab}`}
          aria-labelledby={`tab-${tab}`}
        >
            {tab === 'consultation' ? (
              <ConsultationPanel patient={patient.data} visitId={visitId} />
            ) : history.isLoading ? (
              <Spinner label="Loading visits" />
            ) : tab === 'history' ? (
              visits.length === 0 ? (
                <div className="p-5">
                  <EmptyState
                    icon={<Icon name="file" size="2rem" />}
                    title="No consultations recorded yet"
                    body="Record this consultation to start their history."
                  />
                </div>
              ) : (
                <>
                  <ul className="m-0 list-none p-0">
                    {visits.map((visit) => (
                      <VisitCard
                        key={visit.id}
                        visit={visit}
                        // An accordion: the newest is open, and opening another
                        // closes it. Resolved at render rather than seeded into
                        // state, because the first page arrives after the first
                        // render and there is nothing to seed from yet.
                        expanded={(openVisit ?? visits[0]?.id) === visit.id}
                        onToggle={() =>
                          setOpenVisit((current) =>
                            (current ?? visits[0]?.id) === visit.id ? '' : visit.id,
                          )
                        }
                      />
                    ))}
                  </ul>
                  {history.hasNextPage && (
                    <div className="p-5">
                      <Button
                        variant="secondary"
                        onClick={() => void history.fetchNextPage()}
                        loading={history.isFetchingNextPage}
                      >
                        Show older consultations
                      </Button>
                    </div>
                  )}
                </>
              )
            ) : tab === 'prescriptions' ? (
              prescriptions.length === 0 ? (
                <EmptyState icon={<Icon name="pill" size="2rem" />} title="No prescriptions yet" />
              ) : (
                <RecordTable
                  caption={`${prescriptions.length} prescribed medicines`}
                  columns={RECORD_PRESCRIPTION_COLUMNS}
                  rowKey={(item, i) => `${item.visitId}-${item.position}-${i}`}
                  // One group per consultation: co-prescribed drugs are read as
                  // a set, and the date alone cannot separate two visits on the
                  // same day.
                  groups={groupByVisit(prescriptions, (item) => item.visitId).map((group) => ({
                    id: group.visitId,
                    rows: group.items,
                    heading: (
                      <>
                        <span className="font-semibold text-fg-muted">
                          {formatVisitDate(group.items[0]!.visitDate)}
                        </span>
                        <span className="ml-2 text-fg-subtle">
                          {group.items.length}{' '}
                          {group.items.length === 1 ? 'medicine' : 'medicines'}
                        </span>
                      </>
                    ),
                  }))}
                />
              )
            ) : tab === 'tests' ? (
              tests.length === 0 ? (
                <EmptyState icon={<Icon name="file" size="2rem" />} title="No test results yet" />
              ) : (
                <RecordTable
                  caption={`${tests.length} test results`}
                  columns={RECORD_TEST_COLUMNS}
                  rows={tests}
                  rowKey={(item, i) => `${item.visitId}-${item.position}-${i}`}
                />
              )
            ) : documents.length === 0 ? (
              <EmptyState icon={<Icon name="image" size="2rem" />} title="No documents attached" />
            ) : (
              <AttachmentStrip attachments={documents} />
          )}
        </div>
      </main>

      {/* Not while the consultation form is open: offering to start the thing
          you are already typing is a tap that can only lose work. */}
      {tab !== 'consultation' && (
        <div className="sticky bottom-0 border-t border-line bg-surface px-4 pt-3 pb-safe-3 lg:hidden">
          <div className="mx-auto w-full max-w-[60rem]">
            <LinkButton to={`/patients/${patientId}/visits/new`} size="large" block>
              <Icon name="plus" />
              New consultation
            </LinkButton>
          </div>
        </div>
      )}
    </div>
  )
}
